import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import supportTicketService from '../services/supportTicketService';
import SupportTicketsPanel from './support/SupportTicketsPanel';
import partnerService from '../services/partnerService';
import accountService from '../services/accountService';
import hotelService from '../services/hotelService';
import permissionService from '../services/permissionService';
import './SupportPage.css';

const EMPTY_FORM = {
    subject: '', description: '', type: 'issue', priority: 'medium',
    partner_id: '', account_id: '', hotel_id: '',
};

const SupportPage = () => {
    const { isSuperAdmin, isPartner } = useAuth();
    const { selectedPartner } = useAccount();

    const [showForm, setShowForm]         = useState(false);
    const [formData, setFormData]         = useState(EMPTY_FORM);
    const [submitting, setSubmitting]     = useState(false);
    const [formError, setFormError]       = useState(null);
    const [successMsg, setSuccessMsg]     = useState(null);
    const [reloadKey, setReloadKey]       = useState(0);

    // Dropdown lists
    const [partners, setPartners]   = useState([]);
    const [chains, setChains]       = useState([]);
    const [hotels, setHotels]       = useState([]);
    const [loadingChains, setLoadingChains] = useState(false);
    const [loadingHotels, setLoadingHotels] = useState(false);

    // For regular (chain/hotel admin) users, load their accessible chains directly
    const isPrivileged = isSuperAdmin || isPartner;

    // Load partner list when form opens (superadmin / partner users only)
    useEffect(() => {
        if (!showForm) return;
        if (!isPrivileged) {
            // Regular admins: load their chains directly, skip partner picker
            permissionService.getMyChains()
                .then(res => {
                    const list = res.chains || [];
                    setChains(list);
                    if (list.length === 1) setFormData(d => ({ ...d, account_id: list[0].id }));
                })
                .catch(() => setChains([]));
            return;
        }
        const load = async () => {
            try {
                let list;
                if (isSuperAdmin) {
                    const res = await partnerService.getAllPartners();
                    list = res.data?.partners || res.data || [];
                } else {
                    const res = await partnerService.getMyPartners();
                    list = res.data?.partners || [];
                }
                setPartners(list);
                if (list.length === 1) {
                    setFormData(d => ({ ...d, partner_id: list[0].partnerid }));
                } else if (selectedPartner && list.find(p => p.partnerid === selectedPartner)) {
                    setFormData(d => ({ ...d, partner_id: selectedPartner }));
                }
            } catch {
                setPartners([]);
            }
        };
        load();
    }, [showForm]);

    // Load chains when partner changes
    useEffect(() => {
        if (!formData.partner_id) {
            setChains([]);
            setHotels([]);
            setFormData(d => ({ ...d, account_id: '', hotel_id: '' }));
            return;
        }
        const load = async () => {
            setLoadingChains(true);
            try {
                const res = await accountService.getAllAccounts({ partnerid: formData.partner_id, activeOnly: true });
                const list = res.accounts || [];
                setChains(list);
                setFormData(d => ({ ...d, account_id: list.length === 1 ? list[0].id : '', hotel_id: '' }));
                setHotels([]);
            } catch {
                setChains([]);
            } finally {
                setLoadingChains(false);
            }
        };
        load();
    }, [formData.partner_id]);

    // Load hotels when chain changes
    useEffect(() => {
        if (!formData.account_id) {
            setHotels([]);
            setFormData(d => ({ ...d, hotel_id: '' }));
            return;
        }
        const load = async () => {
            setLoadingHotels(true);
            try {
                const res = await hotelService.getAllHotels({ accountid: formData.account_id });
                setHotels(res.hotels || res.data?.hotels || []);
                setFormData(d => ({ ...d, hotel_id: '' }));
            } catch {
                setHotels([]);
            } finally {
                setLoadingHotels(false);
            }
        };
        load();
    }, [formData.account_id]);

    const showSuccess = (msg) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(null), 3000);
    };

    const openForm = () => {
        setFormData(EMPTY_FORM);
        setFormError(null);
        setChains([]);
        setHotels([]);
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.subject.trim() || !formData.description.trim()) {
            setFormError('Subject and description are required');
            return;
        }
        if (isPrivileged && !formData.partner_id) {
            setFormError('Please select a partner');
            return;
        }
        if (!formData.account_id) {
            setFormError('Please select a hotel chain');
            return;
        }
        setSubmitting(true);
        setFormError(null);
        try {
            await supportTicketService.createTicket({
                ...formData,
                partner_id: formData.partner_id  || null,
                account_id: formData.account_id  || null,
                hotel_id:   formData.hotel_id    || null,
            });
            setShowForm(false);
            showSuccess('Ticket submitted successfully');
            setReloadKey((k) => k + 1);
        } catch {
            setFormError('Failed to submit ticket. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="support-page">
            <div className="support-header">
                <div>
                    <h2>Support</h2>
                    <p className="support-subtitle">Submit issues, questions or feedback to the HConnect team</p>
                </div>
                <button className="btn-primary" onClick={openForm}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    New Ticket
                </button>
            </div>

            {successMsg && <div className="support-success">{successMsg}</div>}

            {/* New Ticket Form */}
            {showForm && (
                <div className="support-form-card">
                    <div className="support-form-header">
                        <h3>New Support Ticket</h3>
                        <button className="btn-icon" onClick={() => setShowForm(false)}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {formError && <div className="support-form-error">{formError}</div>}

                        <div className="support-form-row">
                            <div className="support-form-group">
                                <label>Type</label>
                                <select value={formData.type} onChange={e => setFormData(d => ({ ...d, type: e.target.value }))}>
                                    <option value="issue">Issue</option>
                                    <option value="question">Question</option>
                                    <option value="feedback">Feedback</option>
                                </select>
                            </div>
                            <div className="support-form-group">
                                <label>Priority</label>
                                <select value={formData.priority} onChange={e => setFormData(d => ({ ...d, priority: e.target.value }))}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>
                        </div>

                        {/* Context: Partner → Chain → Hotel */}
                        <div className="support-form-section-label">Relates to</div>
                        <div className={`support-form-row ${isPrivileged ? 'support-form-row-3' : ''}`}>
                            {isPrivileged && (
                                <div className="support-form-group">
                                    <label>Partner <span className="required">*</span></label>
                                    <select
                                        value={formData.partner_id}
                                        onChange={e => setFormData(d => ({ ...d, partner_id: e.target.value, account_id: '', hotel_id: '' }))}
                                    >
                                        <option value="">— Select partner —</option>
                                        {partners.map(p => (
                                            <option key={p.partnerid} value={p.partnerid}>
                                                {p.companyname || p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="support-form-group">
                                <label>Hotel Chain <span className="required">*</span></label>
                                <select
                                    value={formData.account_id}
                                    disabled={isPrivileged ? (!formData.partner_id || loadingChains) : loadingChains}
                                    onChange={e => setFormData(d => ({ ...d, account_id: e.target.value, hotel_id: '' }))}
                                >
                                    <option value="">
                                        {loadingChains
                                            ? 'Loading...'
                                            : (isPrivileged && !formData.partner_id)
                                                ? '— Select partner first —'
                                                : '— Select chain —'}
                                    </option>
                                    {chains.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="support-form-group">
                                <label>Hotel</label>
                                <select
                                    value={formData.hotel_id}
                                    disabled={!formData.account_id || loadingHotels}
                                    onChange={e => setFormData(d => ({ ...d, hotel_id: e.target.value }))}
                                >
                                    <option value="">
                                        {loadingHotels ? 'Loading...' : !formData.account_id ? '— Select chain first —' : 'All hotels in chain'}
                                    </option>
                                    {hotels.map(h => (
                                        <option key={h.hotelid} value={h.hotelid}>{h.HOTELNAME}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="support-form-group">
                            <label>Subject <span className="required">*</span></label>
                            <input
                                type="text"
                                placeholder="Brief summary of your issue"
                                value={formData.subject}
                                onChange={e => setFormData(d => ({ ...d, subject: e.target.value }))}
                                maxLength={255}
                            />
                        </div>

                        <div className="support-form-group">
                            <label>Description <span className="required">*</span></label>
                            <textarea
                                rows={5}
                                placeholder="Describe your issue in detail..."
                                value={formData.description}
                                onChange={e => setFormData(d => ({ ...d, description: e.target.value }))}
                            />
                        </div>

                        <div className="support-form-actions">
                            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={submitting}>
                                {submitting ? 'Submitting...' : 'Submit Ticket'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* The tickets: everything for a superadmin, the partner's queue for a partner user, otherwise the ones you raised
                (shared list — support/SupportTicketsPanel). A ticket opens in hub at /support/:id. */}
            <SupportTicketsPanel scope="auto" reloadKey={reloadKey} emptyText='No tickets yet — click "New Ticket" to get help from the HConnect team' />
        </div>
    );
};

export default SupportPage;
